import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, ScrollView, Platform, Modal } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import LoadingScreen from '../../../Common/LoadingScreen';
import { BACKEND_URL } from '../../../../Config/Const';
import BanderaPromo from './BanderaPromo';
import CarruselOfertas from './CarruselOfertas';
import AddCarrito from '../../Header/Carrito/AddCarrito';
import { colores, fuentes, layout } from '../../../../styles/variablesCss';
import Storage from '../../../../utils/Storage';
import { useFavoritos } from '../../../../context/FavoritosContext';
import { useCart } from '../../../../context/CartContext';
import { formatPrecio } from '../../../../utils/formatPrice';


const CONTROL_BLOCK_WIDTH = 130;

const DescripcionProducto = (props) => {
  const navigation = useNavigation();

  // Estados locales - Declaramos TODOS los estados al principio del componente
  const [producto, setProducto] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cantidad, setCantidad] = useState(1); // Valor por defecto que se actualizará cuando cargue el producto
  const [stockError, setStockError] = useState('');
  const [estaEnCarrito, setEstaEnCarrito] = useState(false); // Estado para controlar si el producto está en el carrito
  const [cantidadEnCarrito, setCantidadEnCarrito] = useState(0); // Cantidad en el carrito

  // Usar el hook de favoritos del contexto
  const { esFavorito, toggleFavorito, loading: loadingFavoritos } = useFavoritos();

  // Usar el contexto del carrito para monitorear cambios
  const { productos: productosEnCarrito } = useCart();

  // Estados para la ruta de búsqueda
  const [searchText, setSearchText] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [isBannerSearch, setIsBannerSearch] = useState(false);

  const truncateSegment = (segment = '', limit = 12) => {
    const safeSegment = String(segment);
    return safeSegment.length > limit ? `${safeSegment.slice(0, limit)}...` : safeSegment;
  };

  //console.log('Estado de producto en DescripcionProducto:', props);

  const getBreadcrumbPath = () => {
    const segments = [departmentName, categoryName, subcategoryName]
      .filter(Boolean)
      .map((seg) => truncateSegment(seg, 12));

    if (segments.length > 0) {
      return segments.join('/');
    }

    return producto.categoria || '';

  };

  // Responsividad: detectar ancho de ventana para decidir layout en web móvil
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const isSmallWeb = Platform.OS === 'web' && windowWidth < 768;
  const isLargeDesktop = Platform.OS === 'web' && windowWidth >= 1400;

  // Carrusel de imágenes
  const images = useMemo(() => {
    try {
      const list = Array.isArray(producto?.fotos)
        ? producto.fotos
          .map((f) => (typeof f === 'string' ? f : f?.imagen))
          .filter(Boolean)
        : [];

      // Asegurar que la imagen principal esté incluida
      const main = typeof producto?.foto === 'string' ? producto.foto : producto?.foto?.uri;
      const unique = [];
      if (main) unique.push(main);
      for (const url of list) {
        if (!unique.includes(url)) unique.push(url);
      }
      return unique;
    } catch (_) {
      return producto?.foto ? [producto.foto] : [];
    }
  }, [producto]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showZoomModal, setShowZoomModal] = useState(false);

  useEffect(() => {
    // Reiniciar índice cuando cambia el producto
    setCurrentImageIndex(0);
  }, [producto?.plu]);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  // Obtener el producto o ID de las diferentes fuentes posibles
  let productoParam = null;
  let productoId = null;
  let productoIdFromUrl = null;

  // Si estamos en web, intentamos obtener el producto de sessionStorage o el ID de la URL
  if (Platform.OS === 'web') {
    // Primero extraer el ID de la URL (si existe)
    try {
      const pathname = window.location.pathname;
      const match = pathname.match(/\/(?:producto|productos)\/([^\/]+)/);
      if (match && match[1]) {
        productoIdFromUrl = match[1];
      }
    } catch (error) {
    }

    // Primero intentar recuperar el producto completo de sessionStorage
    try {
      const storedProductData = sessionStorage.getItem('currentProductData');
      if (storedProductData) {
        const parsed = JSON.parse(storedProductData);
        // Solo usar el producto cacheado si coincide con el PLU de la URL.
        // Esto evita que al entrar directo por URL se muestre un producto anterior.
        if (!productoIdFromUrl || String(parsed?.plu) === String(productoIdFromUrl)) {
          productoParam = parsed;
        }
      }
    } catch (error) {
    }

    // Si no hay datos en sessionStorage, intentar extraer el ID de la URL
    if (!productoParam) {
      productoId = productoIdFromUrl;
    }
  }

  // Si viene de props.route.params (React Navigation)
  if (props.route && props.route.params) {
    const { producto: productoRoute, plu } = props.route.params;
    if (productoRoute) {
      productoParam = productoRoute;
    } else if (plu) {
      productoId = plu;
    }
  }

  // useRef para controlar si ya se ha cargado el producto (por ID)
  const productoYaCargado = React.useRef(null);
  const ultimoPluSolicitado = React.useRef(null);
  const cargarProductoAbortRef = React.useRef(null);

  // Función para cargar un producto por ID/PLU
  const cargarProductoPorId = async (id) => {
    try {
      setIsLoading(true);
      const mail = await Storage.getItem('mail') || '';
      const codUsu = await Storage.getItem('codUsu') || '';
      const suc = await Storage.getItem('sucursalId') || '4';

      const pluStr = id !== undefined && id !== null ? String(id) : '';
      if (!pluStr) {
        setError('No se encontró información del producto.');
        setIsLoading(false);
        return;
      }

      // Cancelar cualquier request previo para evitar que una respuesta vieja pise el estado actual
      try {
        if (cargarProductoAbortRef.current) {
          cargarProductoAbortRef.current.abort();
        }
      } catch (_) {
      }

      const controller = new AbortController();
      cargarProductoAbortRef.current = controller;
      ultimoPluSolicitado.current = pluStr;

      // Reutilizamos el endpoint de listado que ya funciona en Productos.js,
      // pero filtrando por PLU para obtener el item.
      const params = new URLSearchParams();
      params.set('suc', String(suc || '4'));
      if (mail && String(mail).trim() !== '') params.set('mail', String(mail));
      if (codUsu && String(codUsu).trim() !== '') params.set('codUsu', String(codUsu));

      // Intentar con parámetros comunes del backend.
      // Si el backend no reconoce 'plu', probamos en fallback con 'codArt'.
      params.set('plu', pluStr);

      let response = await fetch(`${BACKEND_URL}/rest/productos/productos?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      // Fallback: algunos servicios esperan codArt en lugar de plu
      if (!response.ok) {
        params.delete('plu');
        params.set('codArt', pluStr);
        response = await fetch(`${BACKEND_URL}/rest/productos/productos?${params.toString()}`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
      }

      if (!response.ok) {
        throw new Error(`Error al cargar el producto: ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));
      const ds = data?.dsResultado || data?.response?.dsResultado?.dsResultado || null;
      const lista = Array.isArray(ds?.productos)
        ? ds.productos
        : Array.isArray(data?.productos)
          ? data.productos
          : [];

      // Si ya cambió el PLU solicitado, ignorar esta respuesta
      if (ultimoPluSolicitado.current !== pluStr) {
        return;
      }

      if (lista && lista.length > 0) {
        const productoData = lista[0];
        setProducto(productoData);
        // Inicializar la cantidad con el valor mínimo del producto
        if (productoData.minStock) {
          setCantidad(parseFloat(productoData.minStock));
        }
        // Verificar si el producto está en el carrito
        verificarProductoEnCarrito(productoData.plu);
        setIsLoading(false);
      } else {
        setError('No se encontró el producto solicitado.');
        setIsLoading(false);
      }
    } catch (error) {
      // Ignorar aborts (suelen ocurrir al navegar rápido)
      const isAbort = error?.name === 'AbortError';
      if (!isAbort) {
        // Solo mostrar error si el request sigue siendo el vigente
        const pluStr = id !== undefined && id !== null ? String(id) : '';
        if (ultimoPluSolicitado.current === pluStr) {
          setError('No se pudo cargar el producto.');
          setIsLoading(false);
        }
      }
    }
  };

  // Función para verificar si el producto está en el carrito
  const verificarProductoEnCarrito = async (plu) => {
    try {
      // Obtener datos del usuario del storage
      const mail = await Storage.getItem('mail') || '';
      const codUsu = await Storage.getItem('codUsu') || '';
      const suc = await Storage.getItem('sucursalId') || '4';

      // Si no hay mail, no podemos cargar el carrito
      if (!mail) {
        setEstaEnCarrito(false);
        setCantidadEnCarrito(0);
        return;
      }

      // Construir la URL con query parameters
      const queryParams = new URLSearchParams({
        mail: mail,
        codUsu: codUsu,
        suc: suc
      }).toString();

      const apiUrl = `${BACKEND_URL}/rest/productos/carrito?${queryParams}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      const data = await response.json();

      if (data && data.dsResultado && data.dsResultado.productos && data.dsResultado.productos.length > 0) {

        // Convertir PLU a string para comparación segura
        const pluStr = String(plu);

        // Buscar si el producto actual está en el carrito
        const productoEnCarrito = data.dsResultado.productos.find(item => {
          const itemPluStr = String(item.plu);
          return itemPluStr === pluStr;
        });

        if (productoEnCarrito) {
          setEstaEnCarrito(true);
          setCantidadEnCarrito(productoEnCarrito.cantCarrito || 1);
        } else {
          setEstaEnCarrito(false);
          setCantidadEnCarrito(0);
        }
      } else {
        setEstaEnCarrito(false);
        setCantidadEnCarrito(0);
      }
    } catch (err) {
      setEstaEnCarrito(false);
      setCantidadEnCarrito(0);
    }
  };

  // Verificar si el producto está en el carrito cada vez que cambia la lista de productos del carrito
  useEffect(() => {
    if (producto && producto.plu && productosEnCarrito !== undefined) {
      const pluStr = String(producto.plu);

      // Si el carrito está vacío, resetear el estado
      if (productosEnCarrito.length === 0) {
        setEstaEnCarrito(false);
        setCantidadEnCarrito(0);
      } else {
        // Si hay productos, buscar el producto actual
        const productoEnCarrito = productosEnCarrito.find(item => String(item.plu) === pluStr);

        if (productoEnCarrito) {
          setEstaEnCarrito(true);
          setCantidadEnCarrito(productoEnCarrito.cantCarrito || 1);
        } else {
          setEstaEnCarrito(false);
          setCantidadEnCarrito(0);
        }
      }
    }
  }, [producto, productosEnCarrito]); // Nos actualizamos cuando cambia el carrito en el contexto


  useEffect(() => {
    // Evitar cargar múltiples veces el mismo producto
    const currentKey = String((productoParam && productoParam.plu) ? productoParam.plu : (productoId || ''));
    if (productoYaCargado.current && productoYaCargado.current === currentKey) return;

    // Si tenemos el objeto producto completo, lo usamos directamente
    if (productoParam) {
      setProducto(productoParam);
      // Inicializar la cantidad con el valor mínimo del producto
      if (productoParam.minStock) {
        setCantidad(parseFloat(productoParam.minStock));
      }
      // Verificar si el producto está en el carrito
      verificarProductoEnCarrito(productoParam.plu);
      setIsLoading(false);
      productoYaCargado.current = currentKey;

      // Obtener información de navegación de sessionStorage
      if (Platform.OS === 'web') {
        const storedSearchText = sessionStorage.getItem('searchText');
        const storedDepartmentName = sessionStorage.getItem('departmentName');
        const storedCategoryName = sessionStorage.getItem('categoryName');
        const storedSubcategoryName = sessionStorage.getItem('subcategoryName');
        const storedIsBannerSearch = sessionStorage.getItem('isBannerSearch');

        if (storedSearchText) setSearchText(storedSearchText);
        if (storedDepartmentName) setDepartmentName(storedDepartmentName);
        if (storedCategoryName) setCategoryName(storedCategoryName);
        if (storedSubcategoryName) setSubcategoryName(storedSubcategoryName);
        if (storedIsBannerSearch) setIsBannerSearch(storedIsBannerSearch === 'true');
      }
    }
    // Si solo tenemos el ID, hacemos una llamada para obtener los detalles
    else if (productoId) {
      cargarProductoPorId(productoId);
      productoYaCargado.current = currentKey;
    }
    // Si no hay ni producto ni ID, mostramos un error
    else {
      setError('No se encontró información del producto.');
      setIsLoading(false);
    }
  }, [productoParam, productoId]); // Solo se ejecuta cuando cambian estas props

  // Calcular el límite máximo permitido basado en maxStock y stock
  const calcularLimiteMaximo = () => {
    if (!producto) return Infinity;

    // Si hay maxStock definido, ese es el límite
    if (producto.maxStock && producto.maxStock > 0) {
      return parseFloat(producto.maxStock);
    }

    // Si no hay maxStock pero hay stock, el stock es el límite
    if (producto.stock && producto.stock > 0) {
      return parseFloat(producto.stock);
    }

    // Si no hay restricciones, no hay límite
    return Infinity;
  };

  // Verificar si hay suficiente stock antes de incrementar
  const incrementar = () => {
    // Incrementar por el valor de minStock, pero respetando límites
    setCantidad(prev => {
      const incremento = producto.minStock ? parseFloat(producto.minStock) : 1;
      const nuevoValor = parseFloat(prev) + incremento;

      // Obtener el límite máximo permitido (basado en maxStock y stock)
      const limiteMaximo = calcularLimiteMaximo();

      // Verificar si el nuevo valor supera el límite máximo
      if (limiteMaximo !== Infinity && nuevoValor > limiteMaximo) {
        // Si excede el límite, mostramos el error y limitamos el valor
        setStockError(`¡Atención! Has alcanzado el límite máximo (${limiteMaximo}).`);
        return limiteMaximo;
      } else {
        // Si está dentro de los límites, limpiamos cualquier error
        setStockError('');
        return parseFloat(nuevoValor.toFixed(2));
      }
    });
  };

  const decrementar = () => {
    // Solo decrementar si el valor resultante sería mayor o igual que minStock
    const valorMinimo = producto.minStock ? parseFloat(producto.minStock) : 1;
    if (parseFloat(cantidad) >= valorMinimo * 2) {
      setCantidad(prev => {
        const nuevoValor = parseFloat((parseFloat(prev) - valorMinimo).toFixed(2));

        // Calcular el límite máximo permitido
        const limiteMaximo = calcularLimiteMaximo();

        // Limpiar mensaje de error si estamos por debajo del límite
        if (limiteMaximo !== Infinity && nuevoValor <= limiteMaximo) {
          setStockError('');
        }
        return nuevoValor;
      });
    }
  };

  const handleToggleFavorito = () => {
    if (producto && producto.plu) {
      toggleFavorito(producto.plu);
    }
  };

  const handleGoBack = () => {
    // Determinar si estamos en web o móvil y navegar apropiadamente
    if (Platform.OS === 'web') {
      // Para web, volver a la página anterior usando history API
      window.history.back();

      // Alternativamente, si queremos volver a una página específica:
      // window.location.href = '/productos';
    } else {
      // Para móvil, usar React Navigation
      navigation.goBack();
    }
  };

  const minPurchaseQuantity = useMemo(() => {
    const val = parseFloat(producto?.minStock);
    return !isNaN(val) && val > 0 ? val : 1;
  }, [producto?.minStock]);

  const unitBasePrice = useMemo(() => {
    const val = parseFloat(producto?.precio);
    return !isNaN(val) && val > 0 ? val : 0;
  }, [producto?.precio]);

  const regularPriceToShow = useMemo(() => unitBasePrice * minPurchaseQuantity, [unitBasePrice, minPurchaseQuantity]);

  const comboInfo = useMemo(() => {
    const comboArr = Array.isArray(producto?.combo) ? producto.combo : [];
    if (comboArr.length === 0) {
      return {
        tieneCombo: false,
        comboPriceToShow: NaN,
        cancom: 0,
        totalCantObligCombo: 0,
        tipoCombo: null,
        vencom: ''
      };
    }

    const totalCantObligCombo = comboArr.reduce((sum, c) => sum + Number(c?.cantOblig || 0), 0);
    const mainCombo = comboArr[0];
    const tieneCombo = true;
    const tipoCombo = Number(mainCombo?.tipoCombo);
    const cancom = Number(mainCombo?.cancom || 0);
    const esPluRegalo = tipoCombo === 3 && Number(producto?.plu) === Number(mainCombo?.pluRegalo);

    let comboBasePrice = unitBasePrice;
    if (!esPluRegalo) {
      if (tipoCombo === 4 || totalCantObligCombo > 0) {
        comboBasePrice = unitBasePrice;
      } else {
        const precom = Number(mainCombo?.precom);
        comboBasePrice = Number.isFinite(precom) && precom > 0 ? precom : unitBasePrice;
      }
    }

    const comboPriceToShow = comboBasePrice * minPurchaseQuantity;

    return {
      tieneCombo,
      comboPriceToShow,
      cancom,
      totalCantObligCombo,
      tipoCombo,
      vencom: mainCombo?.vencom || ''
    };
  }, [producto?.combo, producto?.plu, unitBasePrice, minPurchaseQuantity]);

  // Mostrar pantalla de carga mientras se busca el producto
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <View style={styles.backButtonContent}>
            <MaterialCommunityIcons name="chevron-left" size={20} color="#e30613" />
            <Text style={styles.backButtonText}>Volver</Text>
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  if (!producto) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Producto no encontrado</Text>
        <Text style={styles.errorDetail}>No se pudo cargar la información del producto. Por favor, inténtelo de nuevo.</Text>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <View style={styles.backButtonContent}>
            <MaterialCommunityIcons name="chevron-left" size={20} color="#e30613" />
            <Text style={styles.backButtonText}>Volver a productos</Text>
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.scrollContainer}>
      {/* Breadcrumb - Migas de pan */}
      <View style={styles.breadcrumb}>
        <TouchableOpacity style={styles.breadcrumbItem} onPress={handleGoBack}>
          <MaterialCommunityIcons name="chevron-left" size={18} color="#e30613" />
          <Text style={[styles.breadcrumbText, { marginLeft: 4 }]}>Volver</Text>
        </TouchableOpacity>
        {/*<View>
          {!getBreadcrumbPath() && <Text style={styles.breadcrumbSeparator}> / </Text>}
        </View>
        <View style={styles.searchTextContainer}>
          {isBannerSearch ? (
            <Text style={styles.searchTermText}>Banner</Text>
          ) : searchText ? (
            <Text style={styles.searchTermText}>"{searchText}"</Text>
          ) : (
            <Text style={styles.searchTermText}>
              {getBreadcrumbPath()}
            </Text>
          )}
        </View>*/}  
      </View>

      <View style={[
        styles.redLine,
        isSmallWeb ? styles.redLineMobile : styles.redLineDesktop
      ]} />

      <View style={[styles.mainContainer, isSmallWeb && styles.mainContainerMobile]}>
        <View style={[
          styles.productLayout,
          isSmallWeb ? styles.productLayoutMobile : styles.productLayoutRow
        ]}>
          <View style={[
            styles.imageColumn,
            isSmallWeb && styles.imageColumnMobile,
            isLargeDesktop && styles.imageColumnLarge
          ]}>
            <View
              style={[
                styles.imageContainer,
                isSmallWeb && styles.imageContainerMobile,
                isLargeDesktop && styles.imageContainerLarge
              ]}
            >
              {/* Bandera de promoción (cucarda) */}
              <BanderaPromo
                combo={producto.combo}
                oferta={producto.oferta}
                producto={producto}
                sucursal={4}
              />

              {/* Imagen principal con navegación */}
              <View style={styles.carouselMainWrapper}>
                <TouchableOpacity onPress={() => setShowZoomModal(true)}>
                  <Image
                    source={{ uri: images[currentImageIndex] || (typeof producto.foto === 'string' ? producto.foto : `${BACKEND_URL}/images/productos/${producto.plu}.jpg`) }}
                    style={[
                      styles.imagen,
                      isSmallWeb
                        ? styles.imagenMobile
                        : isLargeDesktop
                          ? styles.imagenDesktopLarge
                          : styles.imagenDesktop
                    ]}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
                {images.length > 1 && (
                  <>
                    <TouchableOpacity
                      style={[styles.navButton, styles.navButtonLeft]}
                      onPress={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                      accessibilityLabel="Imagen anterior"
                    >
                      <MaterialCommunityIcons name="chevron-left" size={22} color="#333" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.navButton, styles.navButtonRight]}
                      onPress={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                      accessibilityLabel="Imagen siguiente"
                    >
                      <MaterialCommunityIcons name="chevron-right" size={22} color="#333" />
                    </TouchableOpacity>
                  </>
                )}
              </View>

              {/* Mostrar cucardas si existen */}
              {producto.cucarda && Array.isArray(producto.cucarda) && producto.cucarda.length > 0 && (
                <View style={styles.cucardasContainer}>
                  {producto.cucarda.map((item, index) => {
                    // Usar la misma lógica de BannerManager para procesar URLs
                    if (item && item.imagen) {
                      const cucardaUrl = item.imagen.startsWith('http')
                        ? item.imagen
                        : `${BACKEND_URL}${item.imagen}`;

                      return (
                        <Image
                          key={`${producto.plu}_cucarda_${index}`}
                          source={{ uri: cucardaUrl }}
                          style={styles.cucardaImage}
                          resizeMode="contain"
                        />
                      );
                    }
                    return null;
                  })}
                </View>
              )}
            </View>

            {/* Thumbnails */}
            {images.length > 1 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.thumbsRow}>
                {images.map((uri, idx) => (
                  <TouchableOpacity key={`thumb-${idx}`} onPress={() => setCurrentImageIndex(idx)} style={[styles.thumbItem, idx === currentImageIndex && styles.thumbItemSelected]}>
                    <Image source={{ uri }} style={styles.thumbImage} resizeMode="contain" />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>
          {isSmallWeb ? (
            <View style={styles.verticalSeparatorMobile} />
          ) : (
            Platform.OS === 'web' && <View style={styles.verticalSeparator} />
          )}
          <View style={[styles.infoColumn, isSmallWeb && styles.infoColumnMobile]}>
            <View style={[styles.infoContainer, isSmallWeb && styles.infoContainerMobile]}>
              <Text
                style={[
                  styles.nombre,
                  isSmallWeb && [styles.textMobileLeft, styles.nombreMobile]
                ]}
              >
                {producto.descripcion || null}
              </Text>
              <Text
                style={[
                  styles.codigo,
                  isSmallWeb && [styles.textMobileLeft, styles.codigoMobile]
                ]}
              >
                COD: {producto.plu || null}
              </Text>

              {/* Mensaje de vencimiento o información adicional del combo */}
              {comboInfo.tieneCombo && comboInfo.vencom && (
                <Text style={styles.vencomMessage}>{comboInfo.vencom}</Text>
              )}

              {comboInfo.tieneCombo && comboInfo.tipoCombo !== 5 ? (
                <View style={[styles.preciosContainer, isSmallWeb && styles.textMobileLeft]}>
                  {comboInfo.cancom > 1 && comboInfo.tipoCombo !== 4 && (
                    <Text style={[styles.comboCantidadText, isSmallWeb && styles.textMobileLeft]}>
                      Llevando {comboInfo.cancom} pagas c/u
                    </Text>
                  )}
                  <Text style={[styles.precioCombo, isSmallWeb && styles.textMobileLeft]}>
                    $ {formatPrecio(comboInfo.comboPriceToShow)}
                  </Text>
                  {(producto.oferta === 'N' || producto.oferta === 'D') && (
                    <Text style={styles.precioAnterior}>P. Normal $ {formatPrecio(regularPriceToShow)}</Text>
                  )}
                  {producto.precioAnterior ? (
                    <Text style={[styles.precioAnterior, isSmallWeb && styles.textMobileLeft]}>
                      $ {formatPrecio(producto.precioAnterior)}
                    </Text>
                  ) : null}
                  {comboInfo.tipoCombo !== 1 && comboInfo.tipoCombo !== 3 ? (
                    <Text style={[styles.precio, isSmallWeb && styles.textMobileLeft]}>
                      $ {formatPrecio(regularPriceToShow, true)}
                    </Text>
                  ) : null}
                  {producto.cuotasSinInt > 0 && (
                    <View style={[styles.cuotasRow, isSmallWeb && styles.textMobileLeft]}>
                      <Text style={[styles.cuotasSinInteres, isSmallWeb && styles.textMobileLeft]}>
                        {producto.cuotasSinInt} cuotas de $ {formatPrecio(comboInfo.comboPriceToShow / producto.cuotasSinInt)}
                      </Text>
                      <Text style={[styles.cuotasSinInteres, isSmallWeb && styles.textMobileLeft]}> sin interés</Text>
                    </View>
                  )}
                </View>
              ) : (
                <View style={[styles.preciosContainer, isSmallWeb && styles.textMobileLeft]}>
                  {producto.precioAnterior ? (
                    <Text style={[styles.precioAnterior, isSmallWeb && styles.textMobileLeft]}>
                      $ {formatPrecio(producto.precioAnterior)}
                    </Text>
                  ) : null}
                  <Text style={[styles.precio, isSmallWeb && styles.textMobileLeft]}>
                    $ {formatPrecio(regularPriceToShow, true)}
                  </Text>
                  {producto.cuotasSinInt > 0 && (
                    <View style={[styles.cuotasRow, isSmallWeb && styles.textMobileLeft]}>
                      <Text style={[styles.cuotasSinInteres, isSmallWeb && styles.textMobileLeft]}>
                        {producto.cuotasSinInt} cuotas de $ {formatPrecio(regularPriceToShow / producto.cuotasSinInt)}
                      </Text>
                      <Text style={[styles.cuotasSinInteres, isSmallWeb && styles.textMobileLeft]}> sin interés</Text>
                    </View>
                  )}
                </View>
              )}

              {producto.preUnMed ? (
                <Text style={[styles.precioUnitario, isSmallWeb && styles.textMobileLeft]}>
                  x Lt. $ {producto.preUnMed.replace('.', ',')}
                </Text>
              ) : null}

              {/* Mostrar precio sin impuestos solo si NO es combo ni oferta */}
              {!producto.combo && !producto.oferta && producto.precioSinImp > 0 ? (
                <Text style={[styles.precioSinImpuestos, isSmallWeb && styles.textMobileLeft]}>
                  Precio sin impuestos nacionales: $ {producto.precioSinImp.toFixed(2)}
                </Text>
              ) : null}

              {/*producto.oferta ? (
                <Text style={styles.promocionFecha}>Promoción válida hasta {producto.fechaFinPromocion}.</Text>
              ) : null*/}

              <View style={styles.cantidadContainer}>
                {stockError ? <Text style={styles.stockErrorText}>{stockError}</Text> : null}
              </View>

              <View style={[
                styles.botonesContainer,
                !isSmallWeb && styles.botonesContainerDesktop
              ]}>
                {/* Controles de cantidad centrados */}
                <View style={styles.cantidadControlesRow}>
                  <TouchableOpacity
                    style={styles.botonCantidad}
                    onPress={decrementar}
                  >
                    <Text style={styles.botonText}>-</Text>
                  </TouchableOpacity>

                  <View style={styles.cantidadInput}>
                    <Text style={styles.cantidadText}>{cantidad}</Text>
                  </View>

                  <TouchableOpacity
                    style={styles.botonCantidad}
                    onPress={incrementar}
                  >
                    <Text style={styles.botonText}>+</Text>
                  </TouchableOpacity>
                </View>

                {/* Botón AGREGAR */}
                <View style={[
                  styles.carritoButtonWrapper,
                  isSmallWeb && styles.carritoButtonWrapperMobile
                ]}>
                  <AddCarrito
                    plu={producto.plu}
                    cantidad={parseFloat(cantidad)}
                    style={styles.botonCarrito}
                    productName={producto.nombre || producto.descripcion}
                    productPrice={producto.precio}
                    productCategory={producto.desCat}
                    onSuccess={() => {
                      setEstaEnCarrito(true);
                      setCantidadEnCarrito(prev => prev + parseFloat(cantidad));
                    }}
                  />
                </View>

                {/* Botón FAV */}
                <TouchableOpacity
                  style={[
                    styles.botonFavorito,
                    isSmallWeb && styles.botonFavoritoMobile
                  ]}
                  onPress={handleToggleFavorito}
                  disabled={loadingFavoritos}
                >
                  <MaterialCommunityIcons
                    name={esFavorito(producto.plu) ? "heart" : "heart-outline"}
                    size={22}
                    color={esFavorito(producto.plu) ? colores.rojo : "#666"}
                  />
                  <Text style={[styles.botonFavoritoText, esFavorito(producto.plu) && { color: colores.rojo }]}>FAV</Text>
                </TouchableOpacity>
              </View>

              {/* Mensaje que indica si el producto está en el carrito */}
              {estaEnCarrito && (
                <View style={styles.enCarritoContainer}>
                  <MaterialCommunityIcons name="cart-check" size={18} color={colores.verde} />
                  <Text style={styles.enCarritoText}>Este producto ya está en tu carrito ({cantidadEnCarrito})</Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>

      {/* Sección de Más Información del producto */}
      {Array.isArray(producto.masInfo) && producto.masInfo.length > 0 && (
        <View style={styles.masInfoContainer}>
          <Text style={styles.masInfoTitle}>
            {producto.masInfo[0].titulo || 'Más información'}
          </Text>
          {producto.masInfo[0].descripcion ? (
            <View style={styles.masInfoBlock}>
              {String(producto.masInfo[0].descripcion)
                .split('\n')
                .filter(Boolean)
                .map((linea, idx) => (
                  <Text key={`mi-${idx}`} style={styles.masInfoText}>{linea}</Text>
                ))}
            </View>
          ) : null}
        </View>
      )}

      <View style={styles.redLine} />
      <View style={styles.ofertasRelacionadas}>
        <CarruselOfertas sucursal={4} />
      </View>

      {/* Modal de Zoom */}
      <Modal
        visible={showZoomModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowZoomModal(false)}
      >
        <View style={styles.zoomModalOverlay}>
          <TouchableOpacity 
            style={styles.zoomCloseButton}
            onPress={() => setShowZoomModal(false)}
          >
            <MaterialCommunityIcons name="close" size={32} color="#fff" />
          </TouchableOpacity>
          
          <View style={styles.zoomImageContainer}>
            <Image
              source={{ uri: images[currentImageIndex] || (typeof producto.foto === 'string' ? producto.foto : `${BACKEND_URL}/images/productos/${producto.plu}.jpg`) }}
              style={styles.zoomImage}
              resizeMode="contain"
            />
          </View>

          {/* Navegación de imágenes en el modal */}
          {images.length > 1 && (
            <View style={styles.zoomNavigation}>
              <TouchableOpacity
                style={styles.zoomNavButton}
                onPress={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
              >
                <MaterialCommunityIcons name="chevron-left" size={28} color="#fff" />
              </TouchableOpacity>
              
              <Text style={styles.zoomImageCounter}>
                {currentImageIndex + 1} / {images.length}
              </Text>
              
              <TouchableOpacity
                style={styles.zoomNavButton}
                onPress={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
              >
                <MaterialCommunityIcons name="chevron-right" size={28} color="#fff" />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    marginTop: 20,
    //  alignItems: 'center',
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    marginHorizontal: '4%',
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: 14,
    color: '#e30613',
    fontFamily: fuentes.regular,
  },
  breadcrumbSeparator: {
    fontSize: 14,
    color: '#666',
    marginHorizontal: 5,
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchTermText: {
    fontSize: 14,
    color: '#e30613',
    //fontWeight: 'bold',
    fontFamily: fuentes.bold,
  },
  redLine: {
    height: 1,
    backgroundColor: '#e30613',
    alignSelf: 'center',
    marginVertical: 5,
  },
  redLineMobile: {
    width: '80%',
  },
  redLineDesktop: {
    width: '125%',
  },
  mainContainer: {
    width: '100%',
    paddingVertical: 15,
    paddingHorizontal: layout.contentPadding,
    backgroundColor: '#f5f5f5',
    alignSelf: 'center',
    marginHorizontal: 'auto',
  },
  mainContainerMobile: {
    paddingHorizontal: 5,
  },
  productLayout: {
    backgroundColor: '#fff',
    borderRadius: 5,
    overflow: 'hidden',
    alignItems: 'center',
    width: '100%',
  },
  productLayoutRow: {
    flexDirection: 'row',
    paddingTop: 100,
    paddingBottom: 100,
  },
  productLayoutMobile: {
    flexDirection: 'row',
    paddingVertical: 12,
    alignItems: 'flex-start',
    gap: 12,
  },
  verticalSeparator: {
    width: 1,
    height: '125%',
    backgroundColor: '#e30613',
    alignSelf: 'center',
    marginHorizontal: 10,
  },
  verticalSeparatorMobile: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: '#e30613',
    marginHorizontal: 8,
    borderRadius: 1,
  },
  imageColumn: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  imageColumnLarge: {
    paddingVertical: 12,
  },
  imageColumnMobile: {
    flex: undefined,
    justifyContent: 'flex-start',
    width: 140,
  },
  imageContainer: {
    width: '100%',
    maxWidth: 420,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    // asegurar espacio suficiente para la imagen y miniaturas en desktop
    minHeight: 310,
    paddingVertical: 6,
    alignSelf: 'center',
  },
  imageContainerLarge: {
    aspectRatio: undefined,
    minHeight: 280,
    height: 320,
    paddingVertical: 0,
    maxWidth: 360,
  },
  imageContainerMobile: {
    aspectRatio: undefined,
    minHeight: undefined,
    height: 'auto',
    paddingVertical: 0,
    marginBottom: 0,
  },
  imagen: {
    width: '100%',
    height: '100%',
  },
  imagenDesktop: {
    width: 340,
    height: 340,
    maxWidth: '90%',
    maxHeight: 360,
  },
  imagenDesktopLarge: {
    width: 300,
    height: 300,
    maxWidth: '85%',
    maxHeight: 320,
  },
  imagenMobile: {
    width: 140,
    height: 140,
    maxWidth: '100%',
  },
  infoColumn: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    paddingHorizontal: 20,
    paddingTop: 0,
    paddingBottom: 20,
    backgroundColor: '#fff',
    width: '100%',
    alignItems: 'center',
  },
  infoColumnMobile: {
    marginTop: 0,
    paddingBottom: 10,
    paddingHorizontal: 0,
    alignItems: 'flex-start',
  },
  carouselMainWrapper: {
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.9,
  },
  navButtonLeft: {
    left: 10,
  },
  navButtonRight: {
    right: 10,
  },
  thumbsRow: {
    marginTop: -40,
  },
  thumbItem: {
    width: 64,
    height: 64,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbItemSelected: {
    borderColor: colores.rojo,
  },
  thumbImage: {
    width: '100%',
    height: '100%',
  },
  infoContainer: {
    width: '70%',
    alignItems: 'center',
  },
  infoContainerMobile: {
    width: '100%',
    alignItems: 'flex-start',
  },
  nombre: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
    textTransform: 'uppercase',
    fontFamily: fuentes.bold,
    textAlign: 'center',
  },
  nombreMobile: {
    fontSize: 18,
  },
  codigo: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
    fontFamily: fuentes.regular,
    textAlign: 'center',
  },
  codigoMobile: {
    fontSize: 12,
  },
  preciosContainer: {
    marginBottom: 5,
    alignItems: 'center',
  },
  comboCantidadText: {
    fontSize: 14,
    color: colores.verde,
    fontFamily: fuentes.medium,
    marginBottom: 5,
  },
  precioAnterior: {
    fontSize: 16,
    color: '#666',
    textDecorationLine: 'line-through',
    marginBottom: 5,
    fontFamily: fuentes.regular,
  },
  precio: {
    fontSize: 28,
    fontFamily: fuentes.bold,
    marginBottom: 5,
  },
  precioCombo: {
    fontSize: 28,
    color: colores.rojo,
    fontFamily: fuentes.bold,
    marginBottom: 5,
  },
  cuotasSinInteres: {
    fontSize: 13,
    fontFamily: fuentes.bold,
    color: colores.rojo,
  },
  cuotasRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  precioUnitario: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
    fontFamily: fuentes.regular,
    textAlign: 'center',
  },
  promocionFecha: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10,
    fontFamily: fuentes.regular,
    textAlign: 'center',
  },
  precioSinImpuestos: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10,
    fontFamily: fuentes.regular,
    textAlign: 'center',
  },
  textMobileLeft: {
    textAlign: 'left',
    width: '100%',
  },
  vencomMessage: {
    fontSize: 14,
    color: '#e30613',
    fontFamily: fuentes.bold,
    marginTop: 5,
    marginBottom: 10,
  },
  cantidadContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    marginVertical: 8,
    width: '100%',
  },
  cantidadControlesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  stockErrorText: {
    color: '#ff3b30',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 10,
    width: '100%',
    fontFamily: fuentes.regular,
  },
  botonCantidad: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#d0d0d0',
  },
  botonText: {
    fontSize: 20,
    color: '#333',
    //fontWeight: 'bold',
    fontFamily: fuentes.bold,
  },
  cantidadInput: {
    width: 50,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#d0d0d0',
    backgroundColor: '#fff',
  },
  cantidadText: {
    fontSize: 18,
    fontFamily: fuentes.regular,
  },
  botonesContainer: {
    marginTop: 5,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  botonesContainerDesktop: {
    width: '100%',
  },
  enCarritoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e8f5e9', // Color verde suave para indicar presencia en carrito
    padding: 10,
    borderRadius: 5,
    marginTop: 8,
  },
  enCarritoText: {
    marginLeft: 8,
    color: colores.verde,
    fontFamily: fuentes.medium,
    fontSize: 14,
  },

  leftActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  carritoButtonWrapper: {
    height: 40,
    width: '50%',
  },
  carritoButtonWrapperMobile: {
    width: CONTROL_BLOCK_WIDTH,
    alignSelf: 'flex-start',
  },
  botonFavorito: {
    flexDirection: 'row',
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f7f7f7',
    width: '50%',
    marginTop: 5,
  },
  botonFavoritoMobile: {
    width: CONTROL_BLOCK_WIDTH,
    alignSelf: 'flex-start',
  },
  botonFavoritoFull: {
    width: '100%',
  },
  favRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  favRight: {
    flex: 1,
  },
  // En desktop, alineamos el botón de Favorito bajo el ancho del botón Carrito
  // asumiendo que el bloque de cantidad mide 130px (40 + 50 + 40, sin contar bordes menores)
  botonFavoritoDesktopAligned: {
    alignSelf: 'stretch',
    marginLeft: 130,
  },
  botonFavoritoText: {
    color: '#333',
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  ofertasRelacionadas: {
    padding: 15,
    width: '100%',
    alignSelf: 'center',
  },
  masInfoContainer: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
    backgroundColor: '#fff',
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 5,
    marginTop: 10,
  },
  masInfoTitle: {
    fontSize: 18,
    color: '#333',
    marginBottom: 8,
    fontFamily: fuentes.bold,
    textTransform: 'uppercase',
  },
  masInfoBlock: {
    marginTop: 6,
  },
  masInfoText: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
    marginBottom: 4,
    fontFamily: fuentes.regular,
  },
  errorContainer: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 18,
    color: '#e30613',
    textAlign: 'center',
    marginBottom: 20,
  },
  botonCarrito: {
    backgroundColor: colores.rojo,
    width: '100%',
    marginTop: 2,
    height: 42,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
  },
  cucardasContainer: {
    position: 'absolute',
    left: 10,
    top: '30%',
    transform: [{ translateY: '-50%' }],
    flexDirection: 'column',
    alignItems: 'flex-start',
    zIndex: 10, // Para asegurar que aparezca sobre otros elementos
  },
  cucardaImage: {
    width: 60,
    height: 60,
    marginBottom: 8,
  },
  zoomModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  zoomCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 100,
    padding: 10,
  },
  zoomImageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  zoomImage: {
    width: '100%',
    height: '100%',
    maxWidth: '90vw',
    maxHeight: '80vh',
  },
  zoomNavigation: {
    position: 'absolute',
    bottom: 30,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 20,
    width: '100%',
  },
  zoomNavButton: {
    padding: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomImageCounter: {
    color: '#fff',
    fontSize: 16,
    fontFamily: fuentes.bold,
    minWidth: 60,
    textAlign: 'center',
  },
});

export default DescripcionProducto;
